/**
 * The Settings registry (TRA-1170): one typed list that the sub-nav, the
 * route table, the owner guard, and the impersonation banner all key on.
 * Adding a section is one entry here plus one component file; App.tsx,
 * SettingsLayout.tsx, and api/client.ts do not change.
 *
 * `group` is the sub-nav heading a section sits under. `scope` says whose
 * data it shows. Team-scoped sections read the active team through the
 * API's team dependency, so under staff impersonation they show the
 * impersonated team read-only (the API 403s every impersonated write, the
 * sections hide or disable theirs, and SettingsPageHeader says so). The
 * one user-scoped section, Teams, lists the signed-in user's own teams
 * from /api/auth/me, which never re-scopes, so it stays self-scoped under
 * impersonation and ImpersonationBanner says so. Nothing has to suppress
 * the impersonation header for it: the endpoints Teams calls are keyed by
 * the user, not by the team header.
 *
 * `access: 'owner'` sections are listed for the team owner and for
 * impersonating staff only (TRA-1163: Billing); SectionRoute in
 * SettingsLayout.tsx enforces it. `access: 'employee'` sections are
 * listed for TrainLoop staff only, on any team: a first draft a customer
 * is not meant to see yet (TRA-1513: Task map). Opening one to the team
 * is a change of this one word. `access: 'owner-or-employee'` sections
 * are listed for both: the owner, and TrainLoop staff whether they
 * impersonate the team or belong to it (TRA-1626: Audit log, which
 * records the staff read).
 *
 * `navRoute` names the sidebar page a section configures. A team whose
 * nav config hides that page (TRA-927) has no use for its settings, so
 * the section hides with it, in the sub-nav and at its URL: the Jobs
 * sections all feed the Review page. Sections without one are listed for
 * every team. TeamSidebarSection shows staff which sections an entry carries.
 *
 * Copy gate: `label` and `description` render in PageHeader, so the
 * CLAUDE.md rules apply (no em dashes, no trailing tails).
 */

import {
  Bell,
  Bot,
  Building2,
  FileText,
  KeyRound,
  Lock,
  Network,
  Receipt,
  ScanSearch,
  ScrollText,
  Tag,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react'

import { BILLING_ENABLED, NOTIFICATIONS_ENABLED } from '@/config'
import { isNavRouteVisible } from '@/lib/nav'

import { ApiKeysSection } from './api-keys/ApiKeysSection'
import { AuditLogSection } from './audit-log/AuditLogSection'
import { BillingSection } from './BillingSection'
import { GithubAppSection } from './github-app/GithubAppSection'
import { JobMapSection } from './job-map/JobMapSection'
import { MembersSection } from './members/MembersSection'
import { TrainLoopSection } from './train-loop/TrainLoopSection'
import { NotificationsSection } from './NotificationsSection'
import { PromptsSection } from './PromptsSection'
import { DreamScanSection } from './review-scans/DreamScanSection'
import { RolloutSecretsSection } from './RolloutSecretsSection'
import { TagsSection } from './TagsSection'
import { TeamsSection } from './TeamsSection'

/** Sub-nav headings. Jobs holds what the nightly agents read. */
export type SettingsGroup = 'account' | 'team' | 'jobs'
export type SettingsAccess = 'member' | 'owner' | 'employee' | 'owner-or-employee'
/** Whose data a section shows: the signed-in user's own, or the active team's. */
export type SettingsScope = 'user' | 'team'

export interface SettingsSection {
  /** URL segment under /settings: 'members' renders at /settings/members. */
  path: string
  label: string
  /** The PageHeader description; left out when the title says it all. */
  description?: string
  icon: LucideIcon
  group: SettingsGroup
  scope: SettingsScope
  access: SettingsAccess
  /**
   * The NAV_ENTRIES route this section configures, if any. The team's nav
   * config hides the section together with the page (TRA-927).
   */
  navRoute?: string
  Component: React.ComponentType<SettingsSectionProps>
}

/**
 * Every section receives its own registry entry and renders PageHeader from
 * it, so the label and description exist in exactly one place.
 */
export interface SettingsSectionProps {
  section: SettingsSection
}

const TEAMS_SECTION: SettingsSection = {
  path: 'teams',
  label: 'Teams',
  description: 'See the teams you belong to and switch between them.',
  icon: Building2,
  group: 'account',
  scope: 'user',
  access: 'member',
  Component: TeamsSection,
}

const NOTIFICATIONS_SECTION: SettingsSection = {
  path: 'notifications',
  label: 'Notifications',
  description: 'Set where failure alerts from production monitoring are delivered.',
  icon: Bell,
  group: 'team',
  scope: 'team',
  access: 'member',
  Component: NotificationsSection,
}

const BILLING_SECTION: SettingsSection = {
  path: 'billing',
  label: 'Billing',
  description: 'See what this team is charged and how it pays.',
  icon: Receipt,
  group: 'team',
  scope: 'team',
  access: 'owner',
  Component: BillingSection,
}

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  TEAMS_SECTION,
  {
    path: 'members',
    label: 'Members',
    description: 'Manage who has access to this team.',
    icon: Users,
    group: 'team',
    scope: 'team',
    access: 'member',
    Component: MembersSection,
  },
  {
    path: 'api-keys',
    label: 'API keys',
    description: 'Create and revoke the keys your team uses to call the inference gateway and the data API.',
    icon: KeyRound,
    group: 'team',
    scope: 'team',
    access: 'member',
    Component: ApiKeysSection,
  },
  // Enterprise deployments run none of the jobs that send the team alert
  // callback, and a perimeter cannot reach Slack (TRA-1593).
  ...(NOTIFICATIONS_ENABLED ? [NOTIFICATIONS_SECTION] : []),
  // Enterprise deployments have no Stripe, so Billing is not a section
  // there: the same gate the top-level /billing route carried.
  ...(BILLING_ENABLED ? [BILLING_SECTION] : []),
  // The owner and TrainLoop staff (TRA-1626): the record of who read or
  // changed the team's data, TrainLoop staff included. Staff read it on
  // any team they can open, and the read itself is recorded.
  {
    path: 'audit-log',
    label: 'Audit log',
    description: "See who looked at or changed this team's data. TrainLoop staff are listed too.",
    icon: ScrollText,
    group: 'team',
    scope: 'team',
    access: 'owner-or-employee',
    Component: AuditLogSection,
  },
  // Pipeline opens the group: the whole loop, stage by stage, and
  // which stages run for this team. Listed for every team, because the
  // loop's later stages (training, evaluation, promotion) are configured
  // without Review.
  {
    path: 'pipeline',
    label: 'Pipeline',
    description: 'Every stage from traces to a new model, and which ones are on for this team.',
    icon: Workflow,
    group: 'jobs',
    scope: 'team',
    access: 'member',
    Component: TrainLoopSection,
  },
  // The Jobs sections configure the Review page: the nightly judge fills
  // its queue, the review composer writes the tags, and the prompts drive
  // its judge and its chat. A team without Review has nothing to set here.
  {
    path: 'tags',
    label: 'Tags',
    description:
      'Rename or delete the tags your team puts on runs. Changes apply to every run carrying the tag.',
    icon: Tag,
    group: 'jobs',
    scope: 'team',
    access: 'member',
    navRoute: '/review',
    Component: TagsSection,
  },
  {
    path: 'review-scans',
    label: 'Review scans',
    description:
      'Each night a judge reviews a sample of your production trajectories and flags issues in the review queue. Set how many it judges, which traffic it scans, and on which days.',
    icon: ScanSearch,
    group: 'jobs',
    scope: 'team',
    access: 'member',
    navRoute: '/review',
    Component: DreamScanSection,
  },
  // Read only and staff only for now (TRA-1513); customers edit it later.
  {
    path: 'task-map',
    label: 'Task map',
    icon: Network,
    group: 'jobs',
    scope: 'team',
    access: 'employee',
    navRoute: '/review',
    Component: JobMapSection,
  },
  {
    path: 'prompts',
    label: 'Prompts',
    description:
      'Edit the instructions your agents run on. The nightly judge uses edits on its next run. The review chat uses them on the next question.',
    icon: FileText,
    group: 'jobs',
    scope: 'team',
    access: 'member',
    navRoute: '/review',
    Component: PromptsSection,
  },
  // Eval PRs are opened through the team's installation of the TrainLoop
  // GitHub App, so the page that installs and verifies it sits with the
  // other Review settings.
  {
    path: 'github-app',
    label: 'GitHub App',
    description:
      'Install the TrainLoop GitHub App so eval PRs can be opened in your repositories. See where it is installed and which repositories it can reach.',
    icon: Bot,
    group: 'jobs',
    scope: 'team',
    access: 'member',
    navRoute: '/review',
    Component: GithubAppSection,
  },
  // The nightly evals and training runs read these (TRA-1215). Listed for
  // every team: the Evals page is never hidden, and a team stores them
  // before staff arm its nightly.
  {
    path: 'rollout-secrets',
    label: 'Rollout secrets',
    description:
      'Store the environment values your agent needs when TrainLoop runs it for nightly evals and training. Values are encrypted and never shown again.',
    icon: Lock,
    group: 'jobs',
    scope: 'team',
    access: 'member',
    Component: RolloutSecretsSection,
  },
]

/** The section /settings itself opens. */
export const DEFAULT_SETTINGS_SECTION: SettingsSection = TEAMS_SECTION

/** Sub-nav group order and labels. */
export const SETTINGS_GROUPS: readonly SettingsGroup[] = ['account', 'team', 'jobs']
export const GROUP_LABELS: Record<SettingsGroup, string> = {
  account: 'Account',
  team: 'Team',
  jobs: 'Jobs',
}

export const SETTINGS_ROOT = '/settings'

export function settingsPath(path: string): string {
  return `${SETTINGS_ROOT}/${path}`
}

export interface SettingsAccessContext {
  isOwner: boolean
  /** A TrainLoop employee (TeamContext.isEmployee). */
  isEmployee: boolean
  impersonating: boolean
  /** The team's hidden sidebar routes (TRA-927). */
  hiddenNavItems: string[]
  /** Whether the real nav config has arrived (TeamContext.navConfigLoaded). */
  navConfigLoaded: boolean
  /** Whether it has arrived or its request failed (TeamContext.navConfigSettled). */
  navConfigSettled: boolean
}

/** The owner rule: the owner, and impersonating staff, who see the
    section read-only the same way the API lets them read the team's
    billing (TRA-1163). */
function ownerMayOpen(ctx: SettingsAccessContext): boolean {
  return ctx.isOwner || ctx.impersonating
}

/**
 * Whether a section is listed for the signed-in user on the active team.
 * Owner-only sections open to the owner and to impersonating staff.
 * Employee sections open to TrainLoop staff only. Owner-or-employee
 * sections open to either. A section that configures a sidebar page
 * hides whenever the team's nav config hides that page (TRA-927), by
 * the same isNavRouteVisible rule the sidebar applies to the page itself.
 */
export function canAccessSection(
  section: SettingsSection,
  ctx: SettingsAccessContext,
): boolean {
  if (section.access === 'owner' && !ownerMayOpen(ctx)) return false
  if (section.access === 'employee' && !ctx.isEmployee) return false
  if (section.access === 'owner-or-employee' && !ownerMayOpen(ctx) && !ctx.isEmployee) return false
  if (section.navRoute === undefined) return true
  return isNavRouteVisible({
    to: section.navRoute,
    hiddenNavItems: ctx.hiddenNavItems,
    navConfigLoaded: ctx.navConfigLoaded,
  })
}

/** The sections that hide with a sidebar route; TeamSidebarSection lists them. */
export function settingsSectionsForNavRoute(to: string): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((section) => section.navRoute === to)
}

/** The scope of the settings section at a browser path, or null off Settings. */
export function settingsScopeForPath(pathname: string): SettingsScope | null {
  if (!pathname.startsWith(`${SETTINGS_ROOT}/`)) return null
  const segment = pathname.slice(SETTINGS_ROOT.length + 1).split('/')[0]
  return SETTINGS_SECTIONS.find((section) => section.path === segment)?.scope ?? null
}
